import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Board from "../components/Board";
import BoardFullscreenShell from "../components/BoardFullscreenShell";
import GameEndModal from "../components/GameEndModal";
import CoachBanner from "../components/CoachBanner";
import { ScorePanel } from "../components/ScorePanel";
import { getInitialBoardForPlay, getLegalMoves, applyMove, checkWinner, playerHasCapture } from "../game/engine";
import { getCoachHint } from "../game/coach";
import {
  chooseAIMove,
  AI_LEVEL_ORDER,
  AI_LEVELS,
  getAILevel,
  loadSavedAILevel,
  saveAILevel,
  randomThinkDelay,
} from "../game/ai";
import { toast } from "sonner";
import api from "../lib/api";
import { RotateCcw, Flag, BrainCircuit, Maximize2, Minimize2 } from "lucide-react";

const HUMAN = "A";
const AI = "B";

export default function GameAI() {
  const [board, setBoard] = useState(() => getInitialBoardForPlay());
  const [turn, setTurn] = useState("A");
  const [selected, setSelected] = useState(null);
  const [captures, setCaptures] = useState({ A: 0, B: 0 });
  const [moves, setMoves] = useState(0);
  const [winner, setWinner] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [aiThinking, setAiThinking] = useState(false);
  const [levelId, setLevelId] = useState(() => loadSavedAILevel());
  const [animatingMove, setAnimatingMove] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const resultSavedRef = useRef(false);
  const shellRef = useRef(null);

  const level = useMemo(() => getAILevel(levelId), [levelId]);
  const coachingOn = levelId === "easy";

  const allLegal = useMemo(() => (winner ? [] : getLegalMoves(board, turn)), [board, turn, winner]);

  const mustCapturePieces = useMemo(() => {
    if (winner || turn !== HUMAN) return new Set();
    if (!playerHasCapture(board, HUMAN)) return new Set();
    const out = new Set();
    for (const m of allLegal) if (m.captures.length) out.add(`${m.from[0]},${m.from[1]}`);
    return out;
  }, [board, turn, allLegal, winner]);

  const legalForSelected = useMemo(() => {
    if (!selected) return [];
    return allLegal.filter((m) => m.from[0] === selected[0] && m.from[1] === selected[1]);
  }, [allLegal, selected]);

  const coachHint = useMemo(() => {
    if (!coachingOn || winner || turn !== HUMAN || aiThinking) return null;
    return getCoachHint(board, HUMAN);
  }, [coachingOn, board, turn, winner, aiThinking]);

  const saveResult = useCallback(async (w) => {
    if (resultSavedRef.current) return;
    resultSavedRef.current = true;
    const result = w === HUMAN ? "win" : "loss";
    try {
      await api.post("/games/result", {
        mode: "ai",
        result,
        opponent: `IA ${level.label}`,
        moves,
        captures: captures.A,
      });
    } catch (_) { /* ignore */ }
  }, [moves, captures, level.label]);

  // AI move trigger
  useEffect(() => {
    if (winner || turn !== AI) return;
    setAiThinking(true);
    const delay = randomThinkDelay(levelId);
    const id = setTimeout(() => {
      try {
        const m = chooseAIMove(board, AI, levelId);
        if (!m) {
          setWinner(HUMAN);
          saveResult(HUMAN);
          return;
        }
        const movingPiece = board[m.from[1]][m.from[0]];
        const commit = () => {
          const newBoard = applyMove(board, m);
          const newCaptures = { ...captures, B: captures.B + m.captures.length };
          const next = HUMAN;
          const w = checkWinner(newBoard, next);
          setBoard(newBoard);
          setCaptures(newCaptures);
          setMoves((n) => n + 1);
          setLastMove({ from: m.from, to: m.to });
          setAnimatingMove(null);
          if (w) {
            setWinner(w);
            saveResult(w);
          } else {
            setTurn(next);
          }
        };
        if (m.captures.length > 0) {
          setAnimatingMove({
            path: m.path,
            owner: movingPiece.owner,
            king: movingPiece.king,
            duration: 500 + m.captures.length * 250,
            capturedCells: m.captures,
            onDone: commit,
          });
        } else {
          commit();
        }
      } finally {
        setAiThinking(false);
      }
    }, delay);
    return () => clearTimeout(id);
  }, [turn, board, captures, levelId, saveResult, winner]);

  const onClickIntersection = (x, y) => {
    if (winner || turn !== HUMAN || aiThinking || animatingMove) return;
    const piece = board[y][x];
    if (piece && piece.owner === HUMAN) {
      if (coachingOn && mustCapturePieces.size > 0 && !mustCapturePieces.has(`${x},${y}`)) {
        toast.warning("Capture obligatoire.");
        return;
      }
      setSelected([x, y]);
      return;
    }
    if (selected) {
      const m = legalForSelected.find((mv) => mv.to[0] === x && mv.to[1] === y);
      if (!m) return;
      const movingPiece = board[selected[1]][selected[0]];
      const commit = () => {
        const newBoard = applyMove(board, m);
        const newCaptures = { ...captures, A: captures.A + m.captures.length };
        const next = AI;
        const w = checkWinner(newBoard, next);
        setBoard(newBoard);
        setCaptures(newCaptures);
        setMoves((n) => n + 1);
        setLastMove({ from: m.from, to: m.to });
        setSelected(null);
        setAnimatingMove(null);
        if (w) {
          setWinner(w);
          saveResult(w);
        } else {
          setTurn(next);
        }
      };
      if (m.captures.length > 0) {
        setAnimatingMove({
          path: m.path,
          owner: movingPiece.owner,
          king: movingPiece.king,
          duration: 500 + m.captures.length * 250,
          capturedCells: m.captures,
          onDone: commit,
        });
      } else {
        commit();
      }
    }
  };

  const reset = () => {
    setBoard(getInitialBoardForPlay());
    setTurn("A");
    setSelected(null);
    setCaptures({ A: 0, B: 0 });
    setMoves(0);
    setWinner(null);
    setLastMove(null);
    resultSavedRef.current = false;
  };

  const onLevelChange = (nextId) => {
    if (nextId === levelId) return;
    setLevelId(nextId);
    saveAILevel(nextId);
    if (moves > 0 && !winner) {
      toast.info(`Niveau ${AI_LEVELS[nextId].label} — nouvelle partie.`);
      reset();
    }
  };

  const resign = () => {
    if (winner) return;
    setWinner(AI);
    saveResult(AI);
  };

  const toggleFullscreen = () => {
    shellRef.current?.toggleFullscreen();
  };

  const leftPanel = (
    <div className="space-y-3">
      <div className="hidden lg:block">
        <p className="text-[10px] tracking-[0.25em] text-[color:var(--primary)] flex items-center gap-1.5 mb-1">
          <BrainCircuit size={12} /> CONTRE L&apos;IA
        </p>
        <p className="text-xs text-[color:var(--text-secondary)]">Vous : blancs</p>
      </div>
      {coachingOn && (
        <p className="text-[10px] text-[color:var(--primary)]">Mode conseils activé</p>
      )}
      <ScorePanel
        variant="sidebar"
        turn={turn}
        winner={winner}
        captures={captures}
        you="A"
        moves={moves}
      />
      {aiThinking && (
        <p className="text-xs text-[color:var(--primary)] animate-pulse" data-testid="ai-thinking">
          L&apos;IA réfléchit ({level.label})…
        </p>
      )}
      <p className="text-[10px] text-[color:var(--text-secondary)] leading-relaxed hidden sm:block" data-testid="ai-level-description">
        {level.description}
      </p>
    </div>
  );

  const rightPanel = (
    <div className="glass rounded-lg p-4 w-full space-y-3">
      <p className="text-[10px] tracking-[0.25em] text-[color:var(--text-secondary)]">ACTIONS</p>

      <div>
        <label htmlFor="ai-difficulty" className="block text-[10px] tracking-[0.2em] text-[color:var(--text-secondary)] mb-1.5">
          NIVEAU
        </label>
        <select
          id="ai-difficulty"
          value={levelId}
          onChange={(e) => onLevelChange(e.target.value)}
          className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md text-xs px-3 py-2.5"
          data-testid="ai-difficulty"
        >
          {AI_LEVEL_ORDER.map((id) => (
            <option key={id} value={id}>
              {AI_LEVELS[id].label}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        onClick={reset}
        className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border)] text-xs flex items-center justify-center gap-2 hover:bg-[color:var(--surface)]"
        data-testid="ai-reset"
      >
        <RotateCcw size={14} /> Recommencer
      </button>
      <button
        type="button"
        onClick={resign}
        className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border)] text-xs flex items-center justify-center gap-2 hover:bg-[color:var(--surface)]"
        data-testid="ai-resign"
      >
        <Flag size={14} /> Abandonner
      </button>
      <button
        type="button"
        onClick={toggleFullscreen}
        className="w-full px-3 py-2.5 rounded-md border border-[color:var(--border)] text-xs flex items-center justify-center gap-2 hover:bg-[color:var(--surface)]"
        data-testid="board-fullscreen-toggle"
      >
        {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        {isFullscreen ? "Quitter le plein écran" : "Plein écran"}
      </button>
      <Link
        to="/lobby"
        className="block w-full px-3 py-2.5 rounded-md text-center text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface)]"
        data-testid="ai-back-lobby"
      >
        ← Salon
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 fade-up">
        <div className="lg:hidden mb-4">
          <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] flex items-center gap-2">
            <BrainCircuit size={14} /> CONTRE L&apos;IA
          </p>
          <h1 className="text-xl font-bold tracking-tight">Vous jouez les blancs</h1>
        </div>

        <BoardFullscreenShell
          ref={shellRef}
          hideToggle
          onFullscreenChange={setIsFullscreen}
          leftAside={leftPanel}
          rightAside={rightPanel}
        >
          {coachHint && <CoachBanner message={coachHint.message} />}
          <Board
            board={board}
            selectedFrom={selected}
            legalMovesForSelected={legalForSelected}
            mustCapturePieces={mustCapturePieces}
            onClickIntersection={onClickIntersection}
            lastMove={lastMove}
            animatingMove={animatingMove}
            capturedCells={animatingMove?.capturedCells || []}
            hintsOn={coachingOn}
            coachFrom={coachHint?.suggestFrom}
            coachTo={coachHint?.suggestTo}
          />
          <GameEndModal
            open={!!winner}
            mode="ai"
            youWon={winner === "A"}
            winnerSide={winner}
            opponentName={`l'IA (${level.label})`}
            onRestart={reset}
          />
        </BoardFullscreenShell>
      </main>
    </div>
  );
}
